import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { FILTER_TYPE_SELECT } from '@/actionBoards/actionBoard.constants';
import {
  hydrateDrillDimensions,
  nextLevel,
  pruneDrillStateToBoard,
} from '@/actionBoards/actionBoardDrillDown.utilities';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { type ActionsFactory, createStore } from '@seeqdev/utils';
import type { BoardAppliedFilter, DrillStep, RelativeDateRangeSelection } from '@/actionBoards/actionBoard.types';
import type { BoardOutputV1 } from '@seeqdev/api/model/BoardOutputV1';

export interface ActionBoardState {
  boardId: string | undefined;
  board: BoardOutputV1 | undefined;
  tileRefreshQueue: { tileId?: string; token: number }[];
  startDate: string | undefined;
  endDate: string | undefined;
  relativeDateRangeSelection: RelativeDateRangeSelection | null;
  appliedFilters: BoardAppliedFilter[] | null;
  hoveredTileId: string | null;
  aiSummaryBaselineDateRanges: Record<string, { baselineStart: string; baselineEnd: string }>;
  drillState: Record<string, DrillStep[]>;
  breadcrumbsVisible: boolean;
}

export type ActionBoardDehydratedState = Pick<
  ActionBoardState,
  | 'boardId'
  | 'relativeDateRangeSelection'
  | 'appliedFilters'
  | 'aiSummaryBaselineDateRanges'
  | 'drillState'
  | 'breadcrumbsVisible'
> &
  Partial<Pick<ActionBoardState, 'startDate' | 'endDate'>>;

const selectDrillState = (state: ActionBoardState) => state.drillState;

const selectActiveDrillLevels = createSelector(selectDrillState, (drillState) =>
  Object.fromEntries(
    Object.entries(drillState)
      .filter(([, stack]) => stack.length > 0)
      .map(([dimensionId, stack]) => [dimensionId, stack[stack.length - 1].levelId]),
  ),
);

const selectActiveDrillLevelIds = createSelector(selectActiveDrillLevels, (activeDrillLevels) =>
  Object.values(activeDrillLevels).sort(),
);

const selectDrillFilters = createSelector(selectDrillState, (drillState) =>
  Object.values(drillState).flatMap((stack) => stack.flatMap((step) => (step.filter != null ? [step.filter] : []))),
);

const selectMergedFilters = createSelector(
  [(state: ActionBoardState) => state.appliedFilters, selectDrillFilters],
  (applied, drill) => [...(applied ?? []), ...drill],
);

const selectHydratedDrillDimensions = createSelector(
  (state: ActionBoardState) => state.board,
  (board) => hydrateDrillDimensions(board),
);

export interface ActionBoardActions {
  setBoardId: (boardId: string | undefined) => void;
  setBoard: (board: BoardOutputV1) => void;
  queueTileRefresh: (tileId?: string) => void;
  clearTileRefresh: (token: number) => void;
  setDateRange: (startDate: string | undefined, endDate: string | undefined) => void;
  setRelativeDateRangeSelection: (relativeDateRangeSelection: RelativeDateRangeSelection | null) => void;
  setAppliedFilters: (
    filtersOrUpdater:
      | BoardAppliedFilter[]
      | null
      | ((prev: BoardAppliedFilter[] | null) => BoardAppliedFilter[] | null),
  ) => void;
  setHoveredTileId: (tileId: string | null) => void;
  setAiSummaryBaselineDateRange: (tileId: string, baselineStart: string, baselineEnd: string) => void;
  drillDown: (dimensionId: string, clickedLevelId: string, clickedColumnName: string, clickedValue: string) => void;
  rollUpTo: (dimensionId: string, targetLevelId: string | null) => void;
  switchDrillLevel: (dimensionId: string, levelId: string | null) => void;
  setBreadcrumbsVisible: (visible: boolean | ((prev: boolean) => boolean)) => void;
  reset: (overrides?: Partial<ActionBoardState>) => void;
}

const initialState: ActionBoardState = Object.freeze({
  boardId: undefined,
  board: undefined,
  tileRefreshQueue: [],
  startDate: undefined,
  endDate: undefined,
  relativeDateRangeSelection: null,
  appliedFilters: null,
  hoveredTileId: null,
  aiSummaryBaselineDateRanges: {},
  drillState: {},
  breadcrumbsVisible: true,
});

const actionBoardActionsFactory: ActionsFactory<ActionBoardState, ActionBoardActions> = (set, _get, resetState) => ({
  setBoardId: (boardId) =>
    set((draft) => {
      draft.boardId = boardId;
    }),

  setBoard: (board) =>
    set((draft) => {
      draft.board = board;
      draft.boardId = board.id;
      draft.drillState = pruneDrillStateToBoard(draft.drillState, board);
    }),

  queueTileRefresh: (tileId) =>
    set((draft) => {
      draft.tileRefreshQueue.push({ tileId, token: Date.now() + Math.random() });
    }),

  clearTileRefresh: (token) =>
    set((draft) => {
      const nextQueue = draft.tileRefreshQueue.filter((entry) => entry.token !== token);
      if (nextQueue.length !== draft.tileRefreshQueue.length) {
        draft.tileRefreshQueue = nextQueue;
      }
    }),

  setDateRange: (startDate, endDate) =>
    set((draft) => {
      draft.startDate = startDate;
      draft.endDate = endDate;
    }),

  setRelativeDateRangeSelection: (relativeDateRangeSelection) =>
    set((draft) => {
      draft.relativeDateRangeSelection = relativeDateRangeSelection;
    }),

  setAppliedFilters: (filtersOrUpdater) =>
    set((draft) => {
      draft.appliedFilters =
        typeof filtersOrUpdater === 'function' ? filtersOrUpdater(draft.appliedFilters) : filtersOrUpdater;
    }),

  setHoveredTileId: (tileId) =>
    set((draft) => {
      draft.hoveredTileId = tileId;
    }),

  setAiSummaryBaselineDateRange: (tileId, baselineStart, baselineEnd) =>
    set((draft) => {
      draft.aiSummaryBaselineDateRanges[tileId] = { baselineStart, baselineEnd };
    }),

  drillDown: (dimensionId, clickedLevelId, clickedColumnName, clickedValue) =>
    set((draft) => {
      const hydratedDimension = hydrateDrillDimensions(draft.board).find((dimension) => dimension.id === dimensionId);
      if (!hydratedDimension) return;
      const target = nextLevel(hydratedDimension, clickedLevelId);
      if (!target) return;

      const currentStack = draft.drillState[dimensionId] ?? [];
      draft.drillState[dimensionId] = [
        ...currentStack,
        {
          levelId: target.id,
          filter: { columnName: clickedColumnName, filterType: FILTER_TYPE_SELECT, values: [clickedValue] },
        },
      ];
    }),

  rollUpTo: (dimensionId, targetLevelId) =>
    set((draft) => {
      if (targetLevelId === null) {
        delete draft.drillState[dimensionId];
        return;
      }
      const stack = draft.drillState[dimensionId] ?? [];
      const targetIndex = stack.findIndex((step) => step.levelId === targetLevelId);
      if (targetIndex < 0) {
        // Navigating to a level above (or unrelated to) anything drilled — no filter
        // because the user picked the level directly rather than clicking a value.
        draft.drillState[dimensionId] = [{ levelId: targetLevelId, filter: null }];
        return;
      }
      draft.drillState[dimensionId] = stack.slice(0, targetIndex + 1);
    }),

  switchDrillLevel: (dimensionId, levelId) =>
    set((draft) => {
      if (levelId === null) {
        delete draft.drillState[dimensionId];
        return;
      }
      const dimensionStack = draft.drillState[dimensionId] ?? [];
      const selectedLevelIndex = dimensionStack.findIndex((step) => step.levelId === levelId);
      if (selectedLevelIndex >= 0) {
        draft.drillState[dimensionId] = dimensionStack.slice(0, selectedLevelIndex + 1);
        return;
      }
      // Switching to a level below the current one keeps the filters accumulated while drilling;
      // any other jump starts over as a plain filterless group-by switch.
      const dimension = hydrateDrillDimensions(draft.board).find((candidate) => candidate.id === dimensionId);
      const rankOf = (targetLevelId: string | undefined) =>
        dimension?.levels.find((level) => level.id === targetLevelId)?.rank;
      const selectedRank = rankOf(levelId);
      const currentRank = rankOf(dimensionStack.at(-1)?.levelId);
      const isBelowCurrent = selectedRank != null && currentRank != null && selectedRank > currentRank;
      draft.drillState[dimensionId] = isBelowCurrent
        ? [...dimensionStack, { levelId, filter: null }]
        : [{ levelId, filter: null }];
    }),

  setBreadcrumbsVisible: (visible) =>
    set((draft) => {
      draft.breadcrumbsVisible = typeof visible === 'function' ? visible(draft.breadcrumbsVisible) : visible;
    }),

  reset: (overrides) => {
    resetState(overrides);
  },
});

export const dehydrateActionBoardState = (state: ActionBoardState): ActionBoardDehydratedState => ({
  boardId: state.boardId,
  ...(state.relativeDateRangeSelection ? {} : { startDate: state.startDate, endDate: state.endDate }),
  relativeDateRangeSelection: state.relativeDateRangeSelection,
  appliedFilters: state.appliedFilters,
  aiSummaryBaselineDateRanges: state.aiSummaryBaselineDateRanges,
  drillState: state.drillState,
  breadcrumbsVisible: state.breadcrumbsVisible,
});

const [ActionBoardStoreProvider, useActionBoardStoreInstance, useActionBoardSelector] = createStore(
  'ActionBoard',
  initialState,
  actionBoardActionsFactory,
);

export { ActionBoardStoreProvider };

export const useActionBoardActions = () => useActionBoardStoreInstance().actions;
export const useActionBoardBoardId = () => useActionBoardSelector((state) => state.boardId);
export const useActionBoardBoard = () => useActionBoardSelector((state) => state.board);
export const useActionBoardTileRefreshQueue = () => useActionBoardSelector((state) => state.tileRefreshQueue);
export const useActionBoardStartDate = () => useActionBoardSelector((state) => state.startDate);
export const useActionBoardEndDate = () => useActionBoardSelector((state) => state.endDate);
export const useActionBoardRelativeDateRangeSelection = () =>
  useActionBoardSelector((state) => state.relativeDateRangeSelection);
export const useActionBoardAppliedFilters = () => useActionBoardSelector((state) => state.appliedFilters);
export const useActionBoardDrillFilters = () => useActionBoardSelector(selectDrillFilters);
export const useActionBoardMergedFilters = () => useActionBoardSelector(selectMergedFilters);
export const useActionBoardHoveredTileId = () => useActionBoardSelector((state) => state.hoveredTileId);
export const useActionBoardAiSummaryBaselineDateRange = (tileId: string) =>
  useActionBoardSelector((state) => state.aiSummaryBaselineDateRanges[tileId]);
export const useIsActionBoardTileHovered = (tileId: string) =>
  useActionBoardSelector((state) => state.hoveredTileId === tileId);
export const useActionBoardActiveDrillLevels = () => useActionBoardSelector(selectActiveDrillLevels);
export const useActionBoardDrillState = () => useActionBoardSelector(selectDrillState);
export const useHydratedDrillDimensions = () => useActionBoardSelector(selectHydratedDrillDimensions);
export const useActionBoardActiveDrillLevelIds = () => useActionBoardSelector(selectActiveDrillLevelIds);
export const useActionBoardBreadcrumbsVisible = () => useActionBoardSelector((state) => state.breadcrumbsVisible);

export const usePersistActionBoardStore = () => {
  const register = useRegisterPersistedStore();
  const store = useActionBoardStoreInstance();

  useEffect(() => {
    return register({
      storeName: 'sqActionBoardStore',
      persistenceLevel: 'WORKSHEET',
      store,
      dehydrate: dehydrateActionBoardState,
    });
  }, [register, store]);
};
